import countries from './data/countries.json'
import { Locale } from './services/Localization'

export type CountryCode = keyof typeof countries

export type PlanPeriod = keyof IPeriod

export interface IOrganization {
  organizationId: string
  stripeCustomerId: string | null
  stripeInstanceName: 'US' | null
  organizationName: string | null
  workspaceId: string
  workspaceName: string
  workspaceDisplayName: string
  workspacePhoto: string
  workspaceActiveMembersCount: number
  freeTrialExpires: number | null
  userLanguage: Locale
  userId: string
  userAnalyticsEnabled: boolean
}

export interface ICustomer {
  address?: {
    line1: string
    line2: string
    city: string
    state: string
    postalCode: string
    country: CountryCode
  }
  defaultCard?: {
    brand: string
    funding: string
    last4: string
    id: string
  }
  source?: string
}

export interface ISubscriptionPlan {
  id: string
  name: PlanNames
  price: number
  currency: string
  period: keyof IPeriod
}

export interface ICoupon {
  id: string
  name: string
}

export type ActionRequired = {
  clientSecret: string
}

type PlanWithPeriod = IPlan & {
  id: string
  period: PlanPeriod
  price: number
}

export type Subscription = {
  id: string
  customerId: string
  status: string
  quantity: number
  created: number
  currentPeriodEnd: number
  currentPeriodStart: number
  startDate: number
  totalAmount: number
  cancelAtPeriodEnd?: boolean
  coupon?: ICoupon
  actionRequired?: ActionRequired
  scheduledUpdate?:
    | {
        quantity?: number
        plan: ISubscriptionPlan
        totalAmount: number
      }
    | {
        quantity: number
        totalAmount: number
      }
  currentPlan: PlanWithPeriod
}

export interface IInvoice {
  paidOn: number
  planName: PlanNames
  amount: number
  quantity: number
  currency: string
  link: string
}

// TODO: Replace with @taskworld/plan-definition (need availableFeatures to be exported from plan-definition package)
export const availableFeatures = [
  'automation',
  'backup',
  'calendar',
  'chatSearch',
  'collaborationFeedback',
  'freeBackupRetrieval',
  'integrationBox',
  'integrationDropbox',
  'integrationGithub',
  'integrationGoogleDrive',
  'integrationGoogleMeet',
  'integrationMicrosoftTeams',
  'integrationSlack',
  'integrationZapier',
  'overviewDashboard',
  'projectActivity',
  'projectAnalytics',
  'projectArchived',
  'projectCopy',
  'projectExport',
  'projectGroup',
  'projectListColumnView',
  'projectPermissionSetting',
  'projectPinning',
  'projectPrivate',
  'projectTableView',
  'projectTimeline',
  'taskActivityLog',
  'taskCustomFields',
  'taskDependency',
  'taskMultipleLocation',
  'taskNote',
  'taskPoint',
  'taskReminder',
  'taskSubtask',
  'taskTimeTracking',
  'workloadReport',
  'reports',
  'workspaceAnalytics',
  'workspaceTeams',
  'projectTemplate',
] as const

type FeatureName = (typeof availableFeatures)[number]

export enum PlanNames {
  // For testing purposes
  LITE = 'lite',
  // Below are deprecated. The types here are just for legacy purposes.
  FREEMIUM = 'freemium',
  PREMIUM = 'premium',
  PROFESSIONAL = 'professional',
  BUSINESS = 'business',
  BUSINESS_PLUS = 'businessPlus',
  // Current available plans
  BUSINESS_2021 = 'business2021',
  ENTERPRISE = 'enterprise',
}

export interface IPlan {
  name: PlanNames
  recommended?: boolean
  currency: string
  periods?: IPeriod
  minimumUsers: number
  maximumUsers?: number
  features: ReadonlyArray<FeatureName>
  downgradablePlans: Array<string>
  upgradablePlans: Array<string>
  paywalls: Record<
    'activeProjects' | 'guests' | 'storage' | 'attachmentFileSize',
    | {
        limit: number
        warnings?: {
          critical: number
          notice: number
        }
      }
    | undefined
  >
  isFree: boolean
  isDefault: boolean
}

export interface IPeriod {
  month?: {
    id: string
    price: number
  }
  year:
    | {
        id: string
        price: number
        calculatedMonthlyPrice: number
        discountPercentage: number
      }
    | {
        minPrice: number
        maxPrice?: number
      }
}
